import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { colors, typography } from '@99designs/ui/settings';
import { SolidButton, Box, Stack, ProgressBar } from '@99designs/ui';
import { BaseLink, useSnackbar } from '@99designs/purgatory';
import { linkTo, Source, PASSWORD_MIN, PASSWORD_MIN_ADMIN } from '@99designs/feature-auth';
import { FormattedMessage, FormattedReactMessage } from '@99designs/frontend-components/i18n';
import Bugsnag from '@bugsnag/js';
import is99StaffEmail from './common/is99StaffEmail';
import { Layout } from './common/Layout';
import loadable from '@loadable/component';
import { RoleType } from '../../common/constants';
import { useForm } from 'react-hook-form';
import { Radio, TextField } from './Form';
import { BackendErrorType, getErrorForField, setFieldErrors } from './common/utils';
import Captcha from './Captcha';
import { partitionArray } from '@99designs/apps/spa/libs/utils/partitionArray/partitionArray';
import OAuthContinueButton from '@99designs/apps/spa/common/OAuthContinueButton';
import AuthLink from './AuthLink';
import { PageType } from './types';
import { useAuthConfigContext } from './AuthConfigContext';

const PasswordStrengthBar = loadable(
    () => import('../account/components/LogInMethodPage/PasswordStrengthBar/PasswordStrengthBar')
);

export interface SignUpProps {
    email?: string;
    hideRoleSelector?: boolean;
    onSetPage: (
        e: React.MouseEvent<HTMLAnchorElement> | null,
        page: PageType,
        link: string | null
    ) => void;
    onSubmit: (
        endpoint: 'login' | 'signup',
        data: Record<string, unknown>
    ) => Promise<{ status: number; body: any }>;
    showRecaptcha?: boolean;
    source: Source;
    target?: string;
    userRole?: RoleType;
}

interface FormState {
    email: string;
    password: string;
    radio_user: string;
    displayname: string;
    confirmPassword: string;
}

const PuntRoadSubtleText = styled.small`
    color: ${colors.puntRoad};
    font-size: ${typography.typeScale.size2}px;
`;

function SignUp({
    email = '',
    hideRoleSelector = false,
    onSetPage,
    onSubmit,
    showRecaptcha = true,
    source,
    target,
    userRole = 'client',
}: SignUpProps) {
    const { recaptchaSiteKey } = useAuthConfigContext();
    const [recaptchaError, setRecaptchaError] = useState('');

    const recaptchaRef = useRef<any>(null);
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const {
        register,
        handleSubmit,
        watch,
        errors,
        setError,
        formState: { isSubmitting },
        getValues,
    } = useForm({
        defaultValues: {
            email,
            password: '',
            confirmPassword: '',
            radio_user: userRole,
        },
    });
    const snackbar = useSnackbar();

    const currentPassword = watch('password');
    const currentEmail = watch('email');
    const currentRole = watch('radio_user');

    function handleRecaptchaChange(token: string) {
        setRecaptchaError('');
        setRecaptchaToken(token);
    }

    function submitForm({ email, password, radio_user, displayname }: FormState) {
        const needsRecaptcha = showRecaptcha && !recaptchaToken;
        if (recaptchaSiteKey && needsRecaptcha) {
            setRecaptchaError(__("Confirm that you're not a robot"));
            return;
        }

        return onSubmit('signup', {
            email,
            password,
            role: radio_user || currentRole,
            displayName: displayname,
            recaptchaToken,
        })
            .then((d) => {
                if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                }

                if (d.body.errors) {
                    const errors = partitionArray(d.body.errors, (error: BackendErrorType) => {
                        return Object.keys(getValues()).includes(error.param);
                    });
                    errors.nonMatches.forEach((item: BackendErrorType) => {
                        switch (item.param) {
                            case 'captcha':
                                setRecaptchaError(item.message);
                                break;
                            default:
                                snackbar.error(__('Something went wrong, please try again later.'));
                                Bugsnag.notify(`${item.param} : ${item.message}`, (event) => {
                                    event.context = 'Signing up';
                                });
                                break;
                        }
                    });

                    setFieldErrors(errors.matches, setError);
                    return;
                }
            })
            .catch((e) => {
                Bugsnag.notify(e, (event) => {
                    event.context = 'Signing up';
                });
            });
    }

    return (
        <Layout
            title={__('Sign up')}
            description={__('Sign up for a 99designs account')}
            heading={__('Sign up')}
        >
            <form onSubmit={handleSubmit(submitForm)}>
                {!hideRoleSelector && (
                    <Box marginBottom="gutter">
                        <Stack space="quarterGutter">
                            <Radio
                                ref={register}
                                id="radio_client"
                                name="radio_user"
                                value="client"
                                defaultChecked={currentRole === 'client'}
                                data-testid="signup-client"
                            >
                                {__('I need something designed')}
                            </Radio>
                            <Radio
                                ref={register}
                                id="radio_designer"
                                name="radio_user"
                                value="designer"
                                defaultChecked={currentRole === 'designer'}
                                data-testid="signup-designer"
                            >
                                {__("I'm a designer")}
                            </Radio>
                        </Stack>
                    </Box>
                )}
                <Stack space="space8">
                    <TextField
                        key="email"
                        error={getErrorForField('email', errors)}
                        id="email"
                        name="email"
                        type="text"
                        label={__('Email')}
                        ref={register({
                            required: __('Enter your email address'),
                        })}
                        data-testid="signup-email"
                        autoFocus
                        autoComplete="username"
                    />

                    {currentRole === 'designer' && (
                        <TextField
                            key="displayname"
                            error={getErrorForField('displayname', errors)}
                            id="displayname"
                            name="displayname"
                            type="text"
                            label={__('Display name')}
                            data-testid="signup-designer-display-name"
                            ref={register}
                        />
                    )}

                    <Stack space="quarterGutter">
                        <TextField
                            key="password"
                            error={getErrorForField('password', errors)}
                            id="password"
                            name="password"
                            type="password"
                            label={__('Password')}
                            data-testid="signup-password"
                            ref={register({
                                required: __('Enter your password'),
                            })}
                            autoComplete="new-password"
                        />

                        <PasswordStrengthBar
                            password={currentPassword}
                            fallback={<ProgressBar progress={0} small />}
                        />
                        <PuntRoadSubtleText>
                            <FormattedMessage
                                message={__(
                                    'Use {minChars} or more characters with a mix of letters, numbers and symbols'
                                )}
                                values={{
                                    minChars: is99StaffEmail(currentEmail)
                                        ? PASSWORD_MIN_ADMIN
                                        : PASSWORD_MIN,
                                }}
                            />
                        </PuntRoadSubtleText>
                    </Stack>

                    {showRecaptcha && !!recaptchaSiteKey && (
                        <Captcha
                            ref={recaptchaRef}
                            siteKey={recaptchaSiteKey}
                            onChange={handleRecaptchaChange}
                            error={recaptchaError || getErrorForField('captcha', errors)}
                        />
                    )}

                    <PuntRoadSubtleText>
                        <FormattedReactMessage
                            message={__(
                                'By signing up, you agree to our <Link1>Terms of Service</Link1> and have read and acknowledge the <Link2>Privacy Policy</Link2>'
                            )}
                            replacements={{
                                Link1: <BaseLink href={linkTo.termsOfUse()} size="subtle" />,
                                Link2: <BaseLink href={linkTo.privacyPolicy()} size="subtle" />,
                            }}
                        />
                    </PuntRoadSubtleText>

                    <SolidButton
                        variant="primary"
                        width="fill"
                        size="large"
                        processing={isSubmitting}
                        data-testid="signup-button"
                    >
                        {__('Sign up')}
                    </SolidButton>

                    {currentRole === 'client' && (
                        <OAuthContinueButton
                            target={target}
                            action="signup"
                            provider="Vistaprint"
                            source={source}
                        />
                    )}
                </Stack>
            </form>
            <p>
                {__('Already have an account?')}{' '}
                <AuthLink onSetPage={onSetPage} page="login" to={linkTo.login(target)}>
                    {__('Log in')}
                </AuthLink>
            </p>
        </Layout>
    );
}

export default SignUp;
